"use client";

import { useState, useMemo, useRef, useEffect } from "react";
import axios from "axios";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ColumnDef } from "@tanstack/react-table";
import { DATA_KEYS, useRemoteData } from "@/utils/useRemoteData";
import { EnvService } from "@/services/Environment/Environment";
import { useSpotUser } from "@/utils/spotUser";
import { DeleteModal } from "../../Modals/DeleteModal";
import { SpotToast } from "../../SpotToast";
import { DataTable } from "@/components/DataTable";
import { NoxusEmptyBlock } from "@/components/UI/NoxusEmptyBlock";
import type { IMenuEntry } from "@/components/UI/NoxusMenu";
import type { Group, IntegrationsInfoResponse } from "@/generated";
import {
  Pencil,
  Unplug,
  User,
  Plus,
  Link2,
  Info,
  KeyRound,
  Braces,
  SlidersHorizontal,
  SearchX,
  Wrench,
  Plug,
  Fingerprint,
} from "lucide-react";
import { X } from "lucide-react";
import { Button } from "../../shadcn/button";
import { Badge } from "../../shadcn/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../shadcn/select";
import { Dialog, DialogContent, DialogTitle } from "../../shadcn/dialog";
import { SearchBar } from "../../SearchBar";
import { formatDistanceToNow } from "date-fns";
import { cn } from "@/lib/utils";

import type { ActiveCredential, ModalSection } from "./types";
import { buildActiveCredentials } from "./utils";
import { useStartIntegrationOAuth } from "./useStartIntegrationOAuth";
import { CredentialStatusBadge } from "./CredentialStatusBadge";
import { AddIntegrationModal } from "./AddIntegrationModal";
import { InModalCredentialEdit } from "./InModalCredentialEdit";
import { CredentialDetailsPanel } from "./CredentialDetailsPanel";
import { CredentialMetadataPanel } from "./CredentialMetadataPanel";
import { CredentialCustomizationPanel } from "./CredentialCustomizationPanel";
import { MCPToolsPanel } from "./MCPToolsPanel";
import { MCPIdentityPanel } from "./MCPIdentityPanel";
import { timeAgo } from "@/utils/generic";

// ---------------------------------------------------------------------------
// Status filter constants
// ---------------------------------------------------------------------------

const STATUS_FILTER_OPTIONS = [
  "All",
  "Connected",
  "Reconnect needed",
  "Expired",
  "Disabled",
  "Invalid",
] as const;

const STATUS_FILTER_MAP: Record<string, string> = {
  Connected: "active",
  "Reconnect needed": "needs_reconnect",
  Expired: "expired",
  Disabled: "disabled",
  Invalid: "invalid",
};

// ---------------------------------------------------------------------------
// LinkedAccounts
// ---------------------------------------------------------------------------

export const LinkedAccounts = ({
  group,
  isLoading: externalLoading,
  isInSettings,
  isDisabled,
}: {
  group?: Group;
  isLoading?: boolean;
  isInSettings?: boolean;
  isDisabled?: boolean;
}) => {
  const { spotGroup } = useSpotUser();
  const queryClient = useQueryClient();
  const router = useRouter();
  const [searchValue, setSearchValue] = useState("");

  const targetGroupId = isInSettings ? group?.id || "" : spotGroup.id;
  const enabled = isInSettings ? !!group?.id : !!spotGroup.id;

  // Fetch integrations info
  const { data, isLoading } = useQuery<IntegrationsInfoResponse>({
    queryKey: [
      DATA_KEYS.INTEGRATIONS_CONFIG,
      { path: { group_id: targetGroupId } },
    ],
    queryFn: async () => {
      const resp = await axios.get(
        `/api/backend/groups/${targetGroupId}/integrations/config`
      );
      return resp.data;
    },
    enabled,
  });

  const activeCredentials = useMemo(
    () => buildActiveCredentials(data?.integrations ?? []),
    [data]
  );

  // Filter state
  const [filterConnectedBy, setFilterConnectedBy] = useState<string>("all");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [credPage, setCredPage] = useState(1);
  const [credPageSize, setCredPageSize] = useState(10);

  const connectedByUsers = useMemo(() => {
    const users = new Set<string>();
    for (const cred of activeCredentials) {
      if (cred.connectedBy) users.add(cred.connectedBy);
    }
    return Array.from(users).sort();
  }, [activeCredentials]);

  const filteredCredentials = useMemo(() => {
    const q = searchValue.trim().toLowerCase();
    return activeCredentials
      .filter((cred) => {
        if (!q) return true;
        return (
          cred.integration.display_name.toLowerCase().includes(q) ||
          (cred.displayName ?? "").toLowerCase().includes(q) ||
          (cred.connectedBy ?? "").toLowerCase().includes(q)
        );
      })
      .filter((cred) => {
        if (filterConnectedBy === "all") return true;
        return cred.connectedBy === filterConnectedBy;
      })
      .filter((cred) => {
        if (filterStatus === "all") return true;
        const status = cred.status ?? "active";
        return status === filterStatus;
      });
  }, [activeCredentials, searchValue, filterConnectedBy, filterStatus]);

  const paginatedCredentials = useMemo(
    () =>
      filteredCredentials.slice(
        (credPage - 1) * credPageSize,
        credPage * credPageSize
      ),
    [filteredCredentials, credPage, credPageSize]
  );

  const baseUrl = EnvService.getVar("FRONTEND_URL");
  const returnTo = `${baseUrl}/manage-workspace?tab=linked-accounts`;

  // Modal state
  const [addOpen, setAddOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<ActiveCredential | null>(
    null
  );
  const [drawerCred, setDrawerCred] = useState<ActiveCredential | null>(null);
  const drawerCredRef = useRef<ActiveCredential | null>(null);
  if (drawerCred) drawerCredRef.current = drawerCred;
  const [modalSection, setModalSection] = useState<ModalSection>("details");
  const { data: identityInfo } = useRemoteData(
    DATA_KEYS.MCP_IDENTITY,
    {
      path: {
        group_id: targetGroupId,
        credential_id: drawerCred?.credentialId ?? "",
      },
    },
    { enabled: drawerCred?.integration.type === "mcp_custom" }
  );
  const [, setMetaExpanded] = useState(false);
  const { start: startOAuth, adminSetupDialog } = useStartIntegrationOAuth({
    groupId: targetGroupId,
    returnTo,
  });

  // Keep drawerCred in sync with the live query data
  useEffect(() => {
    if (!drawerCred) return;
    const updated = activeCredentials.find(
      (c) => c.credentialId === drawerCred.credentialId
    );
    if (updated && updated !== drawerCred) setDrawerCred(updated);
  }, [activeCredentials, drawerCred]);

  const clearFilters = () => {
    setSearchValue("");
    setFilterConnectedBy("all");
    setFilterStatus("all");
    setCredPage(1);
  };

  const invalidate = () => {
    queryClient.invalidateQueries({
      queryKey: [DATA_KEYS.INTEGRATIONS_CONFIG],
    });
    queryClient.invalidateQueries({ queryKey: [DATA_KEYS.INTEGRATIONS] });
  };

  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const handleDisconnect = async () => {
    if (!pendingDelete) return;
    setIsDisconnecting(true);
    try {
      await axios.delete(
        `/api/backend/groups/${targetGroupId}/integrations/credentials/${pendingDelete.credentialId}`
      );
      invalidate();
      SpotToast.success("Disconnected successfully.");
    } catch {
      SpotToast.error("Failed to disconnect. Please try again.");
    } finally {
      setIsDisconnecting(false);
      setPendingDelete(null);
    }
  };

  const handleEditOAuth = (
    cred: ActiveCredential,
    connectionConfig?: Record<string, unknown>
  ) =>
    startOAuth({
      integration: cred.integration,
      provider: cred.provider,
      credentialId: cred.credentialId,
      connectionConfig,
    });

  const loading = isLoading || externalLoading || !group;

  // -- DataTable columns --
  const credentialColumns: ColumnDef<ActiveCredential, unknown>[] = useMemo(
    () => [
      {
        id: "name",
        header: "Name",
        cell: ({ row }) => {
          const cred = row.original;
          const isMcp = cred.integration.category === "MCP";
          const title = cred.displayName || cred.integration.display_name;
          return (
            <div className="flex items-center gap-3">
              <div className="relative w-7 h-7 shrink-0 flex items-center justify-center">
                {cred.integration.image ? (
                  <Image
                    fill
                    alt={cred.integration.display_name}
                    src={cred.integration.image}
                    style={{ objectFit: "contain" }}
                  />
                ) : (
                  <Plug size={20} className="text-muted-foreground" />
                )}
              </div>
              <div className="flex flex-col min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-sm font-medium truncate">{title}</span>
                  {cred.scope === "server" && (
                    <Badge variant="outline">Server-wide</Badge>
                  )}
                  {isMcp && (
                    <Badge variant="outline">
                      <Plug size={10} className="shrink-0" />
                      MCP
                    </Badge>
                  )}
                </div>
                <span className="text-xs text-muted-foreground truncate">
                  {cred.integration.display_name}
                </span>
              </div>
            </div>
          );
        },
        meta: { width: "35%" },
      },
      {
        id: "connectedBy",
        header: "Connected by",
        cell: ({ row }) => (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <User size={14} />
            <span className="truncate">{row.original.connectedBy ?? "-"}</span>
          </div>
        ),
        meta: { width: "20%" },
      },
      {
        id: "connectedAt",
        header: "Connected at",
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground">
            {row.original.connectedAt ? timeAgo(row.original.connectedAt) : "-"}
          </span>
        ),
        meta: { width: "20%" },
      },
      {
        id: "status",
        header: "Status",
        cell: ({ row }) => (
          <CredentialStatusBadge
            status={row.original.status}
            validationError={
              row.original.meta?.validation_error
                ? String(row.original.meta.validation_error)
                : undefined
            }
          />
        ),
        meta: { width: "15%", noEllipsis: true },
      },
    ],
    []
  );

  // -- Context menu --
  const getContextMenuItems = (cred: ActiveCredential): IMenuEntry[] =>
    cred.scope === "server"
      ? []
      : [
          {
            label: "Edit",
            icon: Pencil,
            onClick: () => {
              setDrawerCred(cred);
              setModalSection("credentials");
            },
          },
          {
            label: "Disconnect",
            icon: Unplug,
            color: "destructive",
            onClick: () => setPendingDelete(cred),
          },
        ];

  return (
    <div>
      {/* Top Bar */}
      <div className="flex items-center justify-between gap-2 mb-4">
        <SearchBar
          placeholder="Search connections"
          value={searchValue}
          setValue={(v: string) => setSearchValue(v)}
          isDebounced
        />
        <div className="flex items-center gap-2">
          <Select
            value={filterConnectedBy}
            onValueChange={(v) => {
              setFilterConnectedBy(v);
              setCredPage(1);
            }}
          >
            <SelectTrigger className="w-[160px] bg-background">
              <SelectValue placeholder="Connected by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All users</SelectItem>
              {connectedByUsers.map((user) => (
                <SelectItem key={user} value={user}>
                  {user}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={filterStatus}
            onValueChange={(v) => {
              setFilterStatus(v);
              setCredPage(1);
            }}
          >
            <SelectTrigger className="w-[160px] bg-background">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              {STATUS_FILTER_OPTIONS.map((label) => {
                const val = STATUS_FILTER_MAP[label] ?? "all";
                return (
                  <SelectItem key={label} value={val}>
                    {label}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
          {!isDisabled && (
            <Button variant="default" onClick={() => setAddOpen(true)}>
              <Plus size={16} />
              Add connection
            </Button>
          )}
        </div>
      </div>

      {/* Active credentials table */}
      <div className="mb-4">
        <DataTable
          columns={credentialColumns}
          data={paginatedCredentials}
          isLoading={loading}
          loadingRows={5}
          noDataMessage="No results"
          rowOnClick={
            isDisabled ? undefined : (_id, cred) => setDrawerCred(cred)
          }
          contextMenuItems={isDisabled ? undefined : getContextMenuItems}
          emptyState={
            activeCredentials.length === 0 ? (
              <NoxusEmptyBlock
                title="No connections"
                description="Securely connect your existing systems to get started. 50+ integrations available."
                icon={Link2}
                button={!isDisabled ? "Add connection" : undefined}
                buttonIcon={!isDisabled ? Plus : undefined}
                onClick={() => setAddOpen(true)}
                size="sm"
              />
            ) : (
              <NoxusEmptyBlock
                title="No results found"
                description="Try adjusting your search or filters."
                icon={SearchX}
                button="Clear filters"
                buttonIcon={X}
                buttonVariant="outline"
                onClick={clearFilters}
                size="sm"
              />
            )
          }
          tableControls={{
            currentPage: credPage,
            setCurrentPage: setCredPage,
            pageSize: credPageSize,
            onPageSizeChange: (opt) => {
              setCredPageSize(Number(opt.value));
              setCredPage(1);
            },
            data: {
              items: paginatedCredentials,
              total: filteredCredentials.length,
              pages: Math.ceil(filteredCredentials.length / credPageSize),
            },
          }}
        />
      </div>

      <div className="text-center text-xs text-muted-foreground mt-4">
        The accounts authenticated here will be available to everyone in this
        workspace
      </div>

      {/* Add modal */}
      <AddIntegrationModal
        open={addOpen}
        onClose={() => {
          setAddOpen(false);
          invalidate();
        }}
        integrations={data?.integrations ?? []}
        groupId={targetGroupId}
        returnTo={returnTo}
        onConnected={() => invalidate()}
      />

      {/* Disconnect confirmation */}
      <DeleteModal
        isOpen={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        onDelete={handleDisconnect}
        title="Disconnect integration"
        description={`Are you sure you want to disconnect ${pendingDelete?.integration.display_name}?`}
        isLoading={isDisconnecting}
      />

      {/* Connection Details Drawer */}
      <Dialog
        open={!!drawerCred}
        onOpenChange={(v) => {
          if (!v) {
            setDrawerCred(null);
            setModalSection("details");
            setMetaExpanded(false);
          }
        }}
      >
        <DialogContent className="sm:max-w-[720px] p-0 gap-0 overflow-hidden">
          {drawerCredRef.current && (
            <div className="flex h-[520px] min-w-0">
              {/* Sidebar */}
              <div className="w-[220px] border-r border-border flex flex-col shrink-0 bg-muted/30">
                <div className="p-5 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="shrink-0 w-8 h-8 rounded-lg bg-background border border-border/60 flex items-center justify-center p-1.5 shadow-sm">
                      {drawerCredRef.current.integration.image ? (
                        <Image
                          width={20}
                          height={20}
                          alt={drawerCredRef.current.integration.display_name}
                          src={drawerCredRef.current.integration.image}
                          style={{
                            objectFit: "contain",
                            width: "100%",
                            height: "100%",
                          }}
                        />
                      ) : (
                        <Plug size={16} className="text-muted-foreground" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <DialogTitle className="text-md font-semibold truncate leading-tight">
                        {drawerCredRef.current.integration.display_name}
                      </DialogTitle>
                    </div>
                  </div>
                </div>

                <nav className="flex flex-col gap-1 px-3 flex-1">
                  {(
                    [
                      { key: "details" as const, label: "Details", icon: Info },
                      {
                        key: "credentials" as const,
                        label: "Credentials",
                        icon: KeyRound,
                        hidden: drawerCredRef.current.scope === "server",
                      },
                      {
                        key: "tools" as const,
                        label: "Tools",
                        icon: Wrench,
                        hidden:
                          drawerCredRef.current.integration.category !== "MCP",
                      },
                      {
                        key: "identity" as const,
                        label: "Identity token",
                        icon: Fingerprint,
                        hidden: !identityInfo?.enabled,
                      },
                      {
                        key: "metadata" as const,
                        label: "Metadata",
                        icon: Braces,
                      },
                      {
                        key: "customization" as const,
                        label: "Customization",
                        icon: SlidersHorizontal,
                        hidden:
                          drawerCredRef.current.scope === "server" ||
                          (drawerCredRef.current.integration.nodes.filter(
                            (n) => n.visible
                          ).length === 0 &&
                            drawerCredRef.current.integration.triggers.filter(
                              (t) => t.visible
                            ).length === 0),
                      },
                    ] as const
                  )
                    .filter((item) => !("hidden" in item && item.hidden))
                    .map((item) => (
                      <button
                        key={item.key}
                        onClick={() => setModalSection(item.key)}
                        className={cn(
                          "flex items-center gap-2.5 px-3 py-1.5 rounded-md text-sm transition-colors text-left cursor-pointer",
                          modalSection === item.key
                            ? "bg-secondary text-foreground font-medium"
                            : "text-muted-foreground hover:text-foreground hover:bg-secondary/50 font-normal"
                        )}
                      >
                        <item.icon size={16} strokeWidth={1.33} />
                        {item.label}
                      </button>
                    ))}
                </nav>

                <button
                  hidden={drawerCredRef.current.scope === "server"}
                  className={cn(
                    "flex items-center gap-2.5 mx-3 mb-3 px-3 py-1.5 rounded-md text-sm transition-colors text-left cursor-pointer mt-auto text-muted-foreground hover:text-destructive hover:bg-red-50 font-normal"
                  )}
                  onClick={() => {
                    setPendingDelete(drawerCredRef.current);
                    setDrawerCred(null);
                    setModalSection("details");
                  }}
                >
                  <Unplug size={16} strokeWidth="1.33px" />
                  Disconnect
                </button>
              </div>

              {/* Main content */}
              <div className="flex-1 flex flex-col min-w-0 min-h-0">
                {modalSection === "details" && (
                  <CredentialDetailsPanel
                    cred={drawerCredRef.current}
                    groupId={targetGroupId}
                  />
                )}
                {modalSection === "credentials" && (
                  <InModalCredentialEdit
                    cred={drawerCredRef.current}
                    groupId={targetGroupId}
                    onBack={() => {}}
                    onClose={() => {
                      setDrawerCred(null);
                      setModalSection("details");
                      invalidate();
                    }}
                    embedded
                    onReconnect={(_cred, connectionConfig) =>
                      handleEditOAuth(drawerCredRef.current!, connectionConfig)
                    }
                  />
                )}
                {modalSection === "tools" && (
                  <MCPToolsPanel
                    cred={drawerCredRef.current}
                    groupId={targetGroupId}
                    onUpdated={invalidate}
                  />
                )}
                {modalSection === "identity" && identityInfo && (
                  <MCPIdentityPanel info={identityInfo} />
                )}
                {modalSection === "metadata" && (
                  <CredentialMetadataPanel cred={drawerCredRef.current} />
                )}
                {modalSection === "customization" && (
                  <CredentialCustomizationPanel
                    cred={drawerCredRef.current}
                    groupId={targetGroupId}
                    onUpdate={invalidate}
                    onCredChange={setDrawerCred}
                  />
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {adminSetupDialog}
    </div>
  );
};
